import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray } from "react-hook-form";
import {
  DELETE_DOCUMENT_MANIFEST,
  PROCESSED_MANIFESTS_QUERY,
} from "routes/Customer/Waste/Manifests/api";

import { isGeneratorMailingAddressPresent, isGeneratorSiteAddressBlank } from "../generatorAddress";
import { DOCUMENT_MANIFEST_DETAIL, EDIT_DOCUMENT_MANIFEST } from "../queries";
import { ManifestFormValues } from "../types";
import { useDocumentManifestReviewValidation } from "../validationHooks/useDocumentManifestReviewValidation";

function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export function useManifestReviewSection({
  wasteManifestId: wasteManifestIdProp,
  documentId,
}: {
  /** Resolved waste manifest ID — from the dialog prop or the document's sourceWasteManifest */
  wasteManifestId?: string | null;
  /** Current document ID — needed for document deletion */
  documentId?: string;
}) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries: [PROCESSED_MANIFESTS_QUERY, "Documents", "FacilityDocuments"],
  });

  const wasteManifestId = wasteManifestIdProp ?? null;

  const { data: manifestDetailData, loading: manifestDetailLoading } = useQuery(
    DOCUMENT_MANIFEST_DETAIL,
    {
      variables: { wasteManifestId: wasteManifestId ?? "" },
      skip: !wasteManifestId,
      fetchPolicy: "cache-and-network",
    }
  );

  const [editManifestMutation, { loading: manifestSaving }] = useMutation(EDIT_DOCUMENT_MANIFEST, {
    // ProcessedManifestDocuments backs the drawer's Documents section — refetch
    // it too so an edit/confirm reflects there without a manual page refresh.
    refetchQueries: [
      "ProcessedManifests",
      "ProcessedManifestDocuments",
      "DocumentManifestDetailForReview",
    ],
  });

  const [deleteManifestMutation, { loading: deletingManifest }] = useMutation(
    DELETE_DOCUMENT_MANIFEST,
    { refetchQueries: ["ProcessedManifests"] }
  );

  // Map manifest detail data to form values
  const manifestFormValues = useMemo((): ManifestFormValues => {
    const detail = manifestDetailData?.documentManifestDetail;
    const wm = detail?.wasteManifest;
    const wasteLines = detail?.wasteLines ?? [];
    const wasteTransporters = detail?.wasteTransporters ?? [];

    return {
      manifestTrackingNumber: wm?.manifestTrackingNumber ?? "",
      shippedDate: formatDateForInput(wm?.shippedDate),
      receivedDate: formatDateForInput(wm?.receivedDate),
      generatorId: wm?.generatorId ?? "",
      generatorName: wm?.generatorName ?? "",
      generatorMailStreet1: wm?.generatorMailStreet1 ?? "",
      generatorMailStreet2: wm?.generatorMailStreet2 ?? "",
      generatorMailCity: wm?.generatorMailCity ?? "",
      generatorMailState: wm?.generatorMailState ?? "",
      generatorMailZip: wm?.generatorMailZip ?? "",
      generatorTxStateWasteRegistrationId: wm?.generatorTxStateWasteRegistrationId ?? "",
      generatorSiteAddressSameAsMailing: wm?.generatorSiteAddressSameAsMailing ?? null,
      generatorLocationStreet1: wm?.generatorLocationStreet1 ?? "",
      generatorLocationStreet2: wm?.generatorLocationStreet2 ?? "",
      generatorLocationCity: wm?.generatorLocationCity ?? "",
      generatorLocationState: wm?.generatorLocationState ?? "",
      generatorLocationZip: wm?.generatorLocationZip ?? "",
      desFacilityId: wm?.desFacilityId ?? "",
      desFacilityName: wm?.desFacilityName ?? "",
      desFacLocationStreet1: wm?.desFacLocationStreet1 ?? "",
      desFacLocationCity: wm?.desFacLocationCity ?? "",
      desFacLocationState: wm?.desFacLocationState ?? "",
      desFacLocationZip: wm?.desFacLocationZip ?? "",
      discrepancyComments: wm?.discrepancyComments ?? "",
      residueComments: wm?.residueComments ?? "",
      rejectionComments: wm?.rejectionComments ?? "",
      wasteLines: wasteLines.map((wl) => ({
        wasteLineNumber: wl.wasteLineNumber,
        dotHazardous: wl.dotHazardous ?? false,
        dotIdNumberDescription: wl.dotIdNumberDescription ?? "",
        dotPrintedInformation: wl.dotPrintedInformation ?? "",
        nonHazWasteDescription: wl.nonHazWasteDescription ?? "",
        containerNumber: wl.containerNumber ?? null,
        containerTypeCode: wl.containerTypeCode ?? "",
        quantity: wl.quantity ?? null,
        qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode ?? "",
        managementMethodCode: wl.managementMethodCode ?? "",
        federalWasteCodes: (wl.federalWasteCodes ?? []).join(", "),
        stateWasteCodes: (wl.stateWasteCodes ?? []).join(", "),
      })),
      transporters: wasteTransporters.map((t) => ({
        transporterLineNumber: t.transporterLineNumber,
        transporterId: t.transporterId ?? "",
        transporterName: t.transporterName ?? "",
      })),
    };
  }, [manifestDetailData]);

  // On-change EPA-ID validation (format + registered/in-tenant lookups) through
  // the shared compliance-engine validator. Warn-and-allow: issues render inline
  // on the fields but are ERROR-level, so they never block saving.
  const validateManifestReview = useDocumentManifestReviewValidation(wasteManifestId);

  // Issues computed server-side on load, so EPA-ID warnings show on the parsed
  // values before any edit. Subsequent edits re-validate via validateManifestReview.
  const initialIssues = manifestDetailData?.documentManifestDetail?.issues;

  const manifestForm = useValidatingForm<ManifestFormValues>(
    manifestFormValues,
    initialIssues,
    validateManifestReview
  );
  const {
    control: manifestControl,
    handleSubmit: handleManifestSubmit,
    watch,
    setValue,
    issues: manifestIssues,
    formState: { isDirty: manifestIsDirty },
  } = manifestForm;

  const manifestNeedsReview = useMemo(
    () =>
      manifestDetailData?.documentManifestDetail?.wasteManifest?.sourceDocument?.documentMetadata
        ?.needsReview === true,
    [manifestDetailData]
  );

  const facility = useMemo(
    () => manifestDetailData?.documentManifestDetail?.wasteManifest?.facility ?? null,
    [manifestDetailData]
  );

  // Drives the site address "Same address" affordance (EPA Box 5): the site
  // address is left blank when it equals the mailing address. Derived from the
  // loaded data (not live form values) so the affordance doesn't disappear as
  // the reviewer types.
  const generatorSiteAddressInitiallyBlank = useMemo(
    () => isGeneratorSiteAddressBlank(manifestDetailData?.documentManifestDetail?.wasteManifest),
    [manifestDetailData]
  );
  const generatorMailingAddressPresent = useMemo(
    () =>
      isGeneratorMailingAddressPresent(manifestDetailData?.documentManifestDetail?.wasteManifest),
    [manifestDetailData]
  );

  const originalTrackingNumber =
    manifestDetailData?.documentManifestDetail?.wasteManifest?.manifestTrackingNumber ?? "";
  const currentTrackingNumber = watch("manifestTrackingNumber");
  const trackingNumberChanged =
    !!originalTrackingNumber &&
    !!currentTrackingNumber &&
    currentTrackingNumber !== originalTrackingNumber;

  const wasteLineFieldArray = useFieldArray({ control: manifestControl, name: "wasteLines" });
  const transporterFieldArray = useFieldArray({
    control: manifestControl,
    name: "transporters",
  });

  const [deletedWasteLineNumbers, setDeletedWasteLineNumbers] = useState<string[]>([]);
  const [deletedTransporterLineNumbers, setDeletedTransporterLineNumbers] = useState<string[]>([]);

  // Save manifest form data. `markReviewed` decouples persisting edits from
  // confirming the document:
  //  - Confirm / Mark Reviewed passes `true` (clears needsReview on the source
  //    document), allowed even on an unedited doc.
  //  - Partial Save passes `false` (persists edits, keeps the doc in Needs
  //    Review). Only offered when the form is dirty, so it's a no-op otherwise.
  const saveManifest = useCallback(
    async ({ markReviewed = false }: { markReviewed?: boolean } = {}) => {
      if (!wasteManifestId || (!manifestIsDirty && !markReviewed)) return;

      return new Promise<void>((resolve, reject) => {
        handleManifestSubmit(async (values) => {
          try {
            await editManifestMutation({
              variables: {
                wasteManifestId,
                input: {
                  manifestTrackingNumber: values.manifestTrackingNumber || null,
                  shippedDate: values.shippedDate || null,
                  receivedDate: values.receivedDate || null,
                  generatorId: values.generatorId || null,
                  generatorName: values.generatorName || null,
                  generatorMailStreet1: values.generatorMailStreet1 || null,
                  generatorMailStreet2: values.generatorMailStreet2 || null,
                  generatorMailCity: values.generatorMailCity || null,
                  generatorMailState: values.generatorMailState || null,
                  generatorMailZip: values.generatorMailZip || null,
                  generatorTxStateWasteRegistrationId:
                    values.generatorTxStateWasteRegistrationId || null,
                  generatorSiteAddressSameAsMailing: values.generatorSiteAddressSameAsMailing,
                  generatorLocationStreet1: values.generatorLocationStreet1 || null,
                  generatorLocationStreet2: values.generatorLocationStreet2 || null,
                  generatorLocationCity: values.generatorLocationCity || null,
                  generatorLocationState: values.generatorLocationState || null,
                  generatorLocationZip: values.generatorLocationZip || null,
                  desFacilityId: values.desFacilityId || null,
                  desFacilityName: values.desFacilityName || null,
                  desFacLocationStreet1: values.desFacLocationStreet1 || null,
                  desFacLocationCity: values.desFacLocationCity || null,
                  desFacLocationState: values.desFacLocationState || null,
                  desFacLocationZip: values.desFacLocationZip || null,
                  // Box 18a narratives sent verbatim — the resolver's
                  // emptyToNull helper decides clear-vs-keep, so an empty
                  // string must reach it (don't `|| null`).
                  discrepancyComments: values.discrepancyComments,
                  residueComments: values.residueComments,
                  rejectionComments: values.rejectionComments,
                  wasteLines: values.wasteLines.map((wl) => ({
                    wasteLineNumber: wl.wasteLineNumber,
                    dotHazardous: wl.dotHazardous,
                    dotIdNumberDescription: wl.dotIdNumberDescription || null,
                    dotPrintedInformation: wl.dotPrintedInformation || null,
                    nonHazWasteDescription: wl.nonHazWasteDescription || null,
                    containerNumber: wl.containerNumber,
                    containerTypeCode: wl.containerTypeCode || null,
                    quantity: wl.quantity,
                    qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode || null,
                    managementMethodCode: wl.managementMethodCode || null,
                    federalWasteCodes: wl.federalWasteCodes
                      ? wl.federalWasteCodes
                          .split(",")
                          .map((c) => c.trim())
                          .filter(Boolean)
                      : [],
                    stateWasteCodes: wl.stateWasteCodes
                      ? wl.stateWasteCodes
                          .split(",")
                          .map((c) => c.trim())
                          .filter(Boolean)
                      : [],
                  })),
                  transporters: values.transporters.map((t) => ({
                    transporterLineNumber: t.transporterLineNumber,
                    transporterId: t.transporterId || null,
                    transporterName: t.transporterName || null,
                  })),
                  deleteWasteLineNumbers: deletedWasteLineNumbers,
                  deleteTransporterLineNumbers: deletedTransporterLineNumbers,
                  markReviewed,
                },
              },
            });
            setDeletedWasteLineNumbers([]);
            setDeletedTransporterLineNumbers([]);
            resolve();
          } catch (err) {
            reject(err);
          }
        })();
      });
    },
    [
      wasteManifestId,
      manifestIsDirty,
      handleManifestSubmit,
      editManifestMutation,
      deletedWasteLineNumbers,
      deletedTransporterLineNumbers,
    ]
  );

  const manifestLoading = manifestDetailLoading;
  const manifestDataLoaded = !!manifestDetailData?.documentManifestDetail && !manifestDetailLoading;

  // Detach: delete manifest records, keep source document. Internal helper —
  // `handleDetached` (which also refetches the grid) is the public surface.
  const detachManifestData = useCallback(async () => {
    if (!wasteManifestId) return;
    await deleteManifestMutation({ variables: { wasteManifestId } });
  }, [wasteManifestId, deleteManifestMutation]);

  // Detach manifest data and refresh the manifests list. Does not close the
  // parent dialog — the caller decides whether to close, advance to the next
  // doc in a multi-doc review, or stay put.
  const handleDetached = useCallback(async () => {
    await detachManifestData();
    client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
  }, [detachManifestData, client]);

  // Delete the document and all associated manifest data. Does not close the
  // parent dialog (caller decides). Detach must run first due to the RESTRICT
  // FK on WasteManifest.sourceDocumentId. The two steps aren't transactional —
  // each failure mode surfaces a distinct alert so the user knows what state
  // the data is in.
  const handleDeleteDocument = useCallback(async () => {
    if (!documentId) return;
    try {
      await detachManifestData();
    } catch (err) {
      alerts.error(
        "Failed to detach manifest data; the document was not deleted.",
        err instanceof Error ? err : undefined
      );
      throw err;
    }
    try {
      await deleteDocumentMutation({
        variables: { id: documentId, retainAsset: false },
      });
    } catch (err) {
      alerts.error(
        "Manifest data was detached, but deleting the source document failed. Please retry from the document library.",
        err instanceof Error ? err : undefined
      );
      client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
      throw err;
    }
    alerts.success("Document and associated manifest data deleted.");
    client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
  }, [documentId, detachManifestData, deleteDocumentMutation, alerts, client]);

  return {
    manifestDataLoaded,
    manifestLoading,
    manifestSaving,
    deletingManifest,
    manifestNeedsReview,
    trackingNumberChanged,
    manifestControl,
    manifestWatch: watch,
    manifestSetValue: setValue,
    manifestIssues,
    wasteLineFieldArray,
    transporterFieldArray,
    onDeleteWasteLine: (lineNumber: string) =>
      setDeletedWasteLineNumbers((prev) => [...prev, lineNumber]),
    onDeleteTransporter: (lineNumber: string) =>
      setDeletedTransporterLineNumbers((prev) => [...prev, lineNumber]),
    manifestIsDirty,
    wasteManifestId,
    deletingDocument,
    handleDetached,
    handleDeleteDocument,
    saveManifest,
    facility,
    generatorSiteAddressInitiallyBlank,
    generatorMailingAddressPresent,
  };
}
